import { useEffect, useMemo, useState } from 'react'
import { FeatureDemo } from 'components/feature-demo/FeatureDemo'
import {
  featureDemoStorageId,
  shouldShowFeatureDemo,
  writeFeatureDemoDismissed,
  type FeatureDemoChoice,
} from 'components/feature-demo/featureDemoStorage'
import { FEATURE_FLAGS } from '@/config/featureFlags'

export const WEBSITE_APPLY_DEMO_ID = 'website-apply'

const INTRO = {
  title: 'Apply From the Website',
  body: 'Start with your email. If it is new, you fill out the financing application here — we create your portal account when you finish.',
}

const DEMO_STEPS = [
  {
    title: 'Your Email',
    body: 'Type the email you want on the application. We check whether an account already exists as you type.',
  },
  {
    title: 'Complete the Form',
    body: 'If the email is new, continue through the financing steps on this page. No sign-in required yet.',
  },
  {
    title: 'Account Created',
    body: 'After you submit, we create your portal account and email a magic sign-in link. First sign-in asks you to set a password. Already have an account? Use Sign in instead.',
  },
] as const

interface WebsiteApplyOnboardingProps {
  anchorEl: HTMLElement | null
}

export function WebsiteApplyOnboarding({ anchorEl }: WebsiteApplyOnboardingProps) {
  const enabled = FEATURE_FLAGS.websiteApplyOnboarding
  const storageId = featureDemoStorageId(WEBSITE_APPLY_DEMO_ID)
  const [ready, setReady] = useState(false)
  const [visible, setVisible] = useState(false)
  const [demoIndex, setDemoIndex] = useState<number | null>(null)

  useEffect(() => {
    const id = window.setTimeout(() => setReady(true), 400)
    return () => window.clearTimeout(id)
  }, [])

  useEffect(() => {
    if (!ready || !anchorEl) return
    setVisible(shouldShowFeatureDemo(storageId, { enabled }))
  }, [ready, anchorEl, enabled, storageId])

  const step = demoIndex === null ? null : DEMO_STEPS[demoIndex]
  const stepLabel = useMemo(() => {
    if (demoIndex === null) return 'New'
    return `Step ${demoIndex + 1} of ${DEMO_STEPS.length}`
  }, [demoIndex])

  const dismiss = (choice: FeatureDemoChoice) => {
    writeFeatureDemoDismissed(storageId, choice)
    setVisible(false)
    setDemoIndex(null)
  }

  if (!visible || !anchorEl) return null

  if (step && demoIndex !== null) {
    const isLast = demoIndex === DEMO_STEPS.length - 1
    return (
      <FeatureDemo
        open
        anchorEl={anchorEl}
        title={step.title}
        body={step.body}
        stepLabel={stepLabel}
        primaryLabel={isLast ? 'Done' : 'Next'}
        secondaryLabel={demoIndex > 0 ? 'Back' : undefined}
        tertiaryLabel='Skip'
        onPrimary={() => {
          if (isLast) dismiss('completed')
          else setDemoIndex(index => (index ?? 0) + 1)
        }}
        onSecondary={demoIndex > 0 ? () => setDemoIndex(index => (index ?? 1) - 1) : undefined}
        onTertiary={() => dismiss('skipped')}
        placement='bottom'
      />
    )
  }

  return (
    <FeatureDemo
      open
      anchorEl={anchorEl}
      title={INTRO.title}
      body={INTRO.body}
      stepLabel='New'
      primaryLabel='Show Me'
      secondaryLabel='Got It'
      onPrimary={() => setDemoIndex(0)}
      onSecondary={() => dismiss('skipped')}
    />
  )
}
